import { useQuery } from "@tanstack/react-query";
import { fetchAllPaginated } from "@/lib/supabase-paginate";
import {
  calcProjectLeadHourlyRates,
  type ProjectLeadHourlyRateRow,
} from "@/lib/project-lead-hourly-rate";
import type { DashboardPeriod } from "@/lib/dashboard-period";

interface RawProject {
  id: string;
  name: string;
  project_code: string | null;
  completed_at: string | null;
  production_lead_id: string | null;
  bf_actual: number | null;
}

export function useProjectLeadHourlyRates(period: DashboardPeriod, enabled = true) {
  return useQuery({
    queryKey: ["project-lead-hourly-rates", period],
    enabled,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<ProjectLeadHourlyRateRow[]> => {
      const projects = await fetchAllPaginated<RawProject>(
        "projects",
        "id, name, project_code, completed_at, production_lead_id, bf_actual",
        (query) => query
          .eq("project_status", "completed")
          .is("deleted_at", null)
          .not("completed_at", "is", null)
          .not("production_lead_id", "is", null),
      );

      const projectIds = projects.map((project) => project.id);
      const leadIds = [...new Set(projects.flatMap((project) => project.production_lead_id ?? []))];
      const [leads, timeEntries] = await Promise.all([
        leadIds.length === 0
          ? Promise.resolve([])
          : fetchAllPaginated<{ id: string; full_name: string }>(
              "users",
              "id, full_name",
              (query) => query.in("id", leadIds),
            ),
        projectIds.length === 0
          ? Promise.resolve([])
          : fetchAllPaginated<{ project_id: string | null; duration_seconds: number | null }>(
              "time_entries",
              "project_id, duration_seconds",
              (query) => query.in("project_id", projectIds).is("deleted_at", null),
            ),
      ]);

      return calcProjectLeadHourlyRates({
        period,
        leads: leads.map((lead) => ({ id: lead.id, name: lead.full_name })),
        projects: projects.map((project) => ({
          id: project.id,
          name: project.name,
          projectCode: project.project_code,
          completedAt: project.completed_at,
          productionLeadId: project.production_lead_id,
          bfActual: project.bf_actual,
        })),
        timeEntries: timeEntries.map((entry) => ({
          projectId: entry.project_id,
          durationSeconds: entry.duration_seconds,
        })),
      });
    },
  });
}
